import { useEffect, useState } from 'react';
import { db } from '@/lib/db';
import type { Member, MemberPublic, Tournament, TournamentGuest } from '@/lib/db/types';
import { Sheet } from '@/components/shared/Sheet';
import { Avatar } from '@/components/shared/Avatar';
import { InvitePlayersPanel } from '@/components/shared/InvitePlayersPanel';
import { OrganizerActions } from '@/components/shared/OrganizerActions';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';

interface Props {
  tournamentId: string;
  member: Member;
  courseNames: Record<string, string>;
  onClose: () => void;
  onUpdated: () => void;
}

interface LeaderRow {
  member_id: string;
  best_gross?: number | null;
  best_net?: number | null;
  position?: number | null;
}

export function TournamentDetailSheet({ tournamentId, member, courseNames, onClose, onUpdated }: Props) {
  const [event, setEvent] = useState<Tournament | null>(null);
  const [board, setBoard] = useState<LeaderRow[]>([]);
  const [memberMap, setMemberMap] = useState<Record<string, MemberPublic>>({});
  const [allMembers, setAllMembers] = useState<MemberPublic[]>([]);
  const [joined, setJoined] = useState(false);
  const [fieldSize, setFieldSize] = useState(0);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [inviteIds, setInviteIds] = useState<string[]>([]);
  const [guestDraft, setGuestDraft] = useState('');
  const [guestNote, setGuestNote] = useState('');
  const [transferTo, setTransferTo] = useState('');

  const reload = async () => {
    const [t, lb, mine, people] = await Promise.all([
      db.tournaments.get(tournamentId),
      db.tournaments.computeLeaderboard(tournamentId),
      db.tournaments.myJoinedIds(),
      db.members.list({ limit: 100 }),
    ]);
    setEvent(t);
    setBoard(lb);
    setFieldSize(lb.filter((r) => r.best_net != null).length || lb.length);
    setMemberMap(Object.fromEntries(people.map((m) => [m.id, m])));
    setAllMembers(people.filter((m) => m.id !== member.id));
    setJoined(mine.includes(tournamentId));
    setLoading(false);
  };

  useEffect(() => { void reload(); }, [tournamentId]);

  const join = async () => {
    setActing(true);
    try {
      await db.tournaments.join(tournamentId);
      onUpdated();
      await reload();
    } finally {
      setActing(false);
    }
  };

  const leave = async () => {
    setActing(true);
    try {
      await db.tournaments.leave(tournamentId);
      onUpdated();
      onClose();
    } finally {
      setActing(false);
    }
  };

  const sendInvites = async () => {
    if (!inviteIds.length) return;
    setActing(true);
    try {
      await db.tournaments.invite(tournamentId, inviteIds);
      setInviteIds([]);
      onUpdated();
      await reload();
    } finally {
      setActing(false);
    }
  };

  const appendGuestNote = async () => {
    if (!event || !guestNote.trim()) return;
    setActing(true);
    try {
      const line = `Guests: ${guestNote.trim()}.`;
      const description = event.description?.includes('Guests:')
        ? event.description
        : `${line}\n${event.description || ''}`.trim();
      await db.tournaments.updateDescription(tournamentId, description);
      await reload();
      setGuestNote('');
    } finally {
      setActing(false);
    }
  };

  const confirmRemoveTournament = async () => {
    setActing(true);
    try {
      await db.tournaments.cancel(tournamentId);
      setShowDeleteConfirm(false);
      onUpdated();
      onClose();
    } finally {
      setActing(false);
    }
  };

  const transferTournament = async () => {
    if (!transferTo) return;
    setActing(true);
    try {
      await db.tournaments.transferOrganizer(tournamentId, transferTo);
      onUpdated();
      await reload();
      setTransferTo('');
    } finally {
      setActing(false);
    }
  };

  if (loading || !event) {
    return <Sheet title="Tournament" onClose={onClose}><div className="p-4 text-sm text-slate-400">Loading…</div></Sheet>;
  }

  const isOrganizer = event.organizer_id === member.id;
  const canJoin = !joined && (event.status === 'open' || event.status === 'in-progress');
  const pot = event.entry_fee_cents > 0 ? (event.entry_fee_cents / 100) * fieldSize : 0;
  const alreadyInvited = new Set(board.map((r) => r.member_id));
  const invitableMembers = allMembers.filter((m) => !alreadyInvited.has(m.id));
  const configGuests = (event.config?.guests as TournamentGuest[] | undefined) ?? [];
  const allowancePct = event.handicap_allowance != null
    ? `${Math.round(event.handicap_allowance * 100)}%`
    : null;

  return (
    <>
    <Sheet title={event.name} onClose={onClose}>
      <div className="space-y-4 p-4 pb-10">
        <div className="rounded-2xl bg-navy p-4 text-cream">
          <div className="text-xs font-bold uppercase tracking-wider text-gold">{event.status.replace('-', ' ')}</div>
          <div className="mt-1 font-serif text-xl font-bold">{event.name}</div>
          <div className="mt-2 text-sm text-white/70">
            {event.starts_at ? new Date(event.starts_at).toLocaleDateString() : 'TBD'}
            {event.ends_at ? ` – ${new Date(event.ends_at).toLocaleDateString()}` : ''}
            {' · '}{event.course_id ? courseNames[event.course_id] : 'Course TBD'}
          </div>
          <div className="mt-2 text-sm text-white/60">
            {event.format} · {event.scoring_basis}
            {allowancePct ? ` · ${allowancePct} HCP` : ''}
            {' · '}{event.holes === 'full' ? '18 holes' : event.holes === 'front-9' ? 'Front 9' : 'Back 9'}
          </div>
        </div>
        {event.description && <p className="whitespace-pre-wrap text-sm text-slate-600">{event.description}</p>}
        {configGuests.length > 0 && (
          <div>
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Guests</div>
            <div className="space-y-2">
              {configGuests.map((g, i) => (
                <div key={`${g.name}-${i}`} className="flex items-center justify-between rounded-xl bg-white px-3 py-2 shadow-sm">
                  <span className="text-sm font-semibold text-navy">{g.name}</span>
                  <span className="text-xs text-slate-500">
                    {g.handicap != null ? `HCP ${g.handicap}` : 'HCP —'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-xl bg-white p-3 text-center shadow-sm">
            <div className="text-[10px] font-bold uppercase text-slate-400">Field</div>
            <div className="font-serif text-xl font-bold text-navy">{board.length}</div>
          </div>
          <div className="rounded-xl bg-white p-3 text-center shadow-sm">
            <div className="text-[10px] font-bold uppercase text-slate-400">Buy-in</div>
            <div className="font-serif text-xl font-bold text-navy">${(event.entry_fee_cents / 100).toFixed(0)}</div>
          </div>
          <div className="rounded-xl bg-white p-3 text-center shadow-sm">
            <div className="text-[10px] font-bold uppercase text-slate-400">Pot</div>
            <div className="font-serif text-xl font-bold text-gold">${pot}</div>
          </div>
        </div>

        {isOrganizer && event.status !== 'completed' && event.status !== 'cancelled' && (
          <div className="space-y-3">
            <InvitePlayersPanel
              members={invitableMembers}
              selectedIds={inviteIds}
              onToggle={(id) => setInviteIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
              showGuests={false}
              title="Invite players"
              searchPlaceholder="Search members to invite…"
            />
            <button type="button" disabled={acting || !inviteIds.length} onClick={() => void sendInvites()} className="w-full rounded-full bg-navy py-2.5 text-sm font-bold text-cream disabled:opacity-50">
              {acting ? 'Inviting…' : 'Send Invites'}
            </button>
            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Guest names (listed in event details)</div>
              <div className="flex gap-2">
                <input value={guestNote} onChange={(e) => setGuestNote(e.target.value)} placeholder="e.g. John Smith, Jane Doe" className="flex-1 rounded-xl border border-line bg-paper px-3 py-2 text-sm" />
                <button type="button" disabled={acting || !guestNote.trim()} onClick={() => void appendGuestNote()} className="rounded-xl bg-line px-3 text-sm font-semibold">Add</button>
              </div>
            </div>
          </div>
        )}

        <div>
          <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Leaderboard</div>
          {board.length === 0 && <div className="text-sm text-slate-500">No participants yet.</div>}
          {board.map((row, i) => {
            const person = memberMap[row.member_id];
            return (
              <div key={row.member_id} className="mb-2 flex items-center gap-3 rounded-xl bg-white p-3 shadow-sm">
                <div className="w-8 text-center font-serif font-bold text-gold">#{row.position ?? i + 1}</div>
                {person ? <Avatar member={person} size={32} /> : <div className="h-8 w-8 rounded-full bg-slate-100" />}
                <div className="flex-1">
                  <div className="text-sm font-semibold text-navy">{person?.name || 'Member'}</div>
                  <div className="text-xs text-slate-500">HCP {person?.handicap ?? '—'}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-navy">{row.best_net ?? '—'}</div>
                  <div className="text-[10px] text-slate-400">net · gross {row.best_gross ?? '—'}</div>
                </div>
              </div>
            );
          })}
        </div>

        {canJoin && (
          <button type="button" disabled={acting} onClick={() => void join()} className="w-full rounded-full bg-gold py-3 text-sm font-bold text-navy disabled:opacity-50">{acting ? 'Joining…' : 'Join Tournament'}</button>
        )}
        {joined && event.status !== 'completed' && (
          <button type="button" disabled={acting} onClick={() => void leave()} className="w-full rounded-full border border-slate-200 py-3 text-sm font-semibold text-slate-600">Leave Tournament</button>
        )}
        {isOrganizer && event.status !== 'completed' && event.status !== 'cancelled' && (
          <OrganizerActions
            members={allMembers}
            currentMemberId={member.id}
            transferTo={transferTo}
            onTransferToChange={setTransferTo}
            onTransfer={() => void transferTournament()}
            onDelete={() => setShowDeleteConfirm(true)}
            deleteLabel="Delete Tournament"
            transferBusy={acting}
            deleteBusy={acting}
          />
        )}
      </div>
    </Sheet>
    <ConfirmDialog
      open={showDeleteConfirm}
      title="Delete this tournament?"
      message="It will be cancelled."
      confirmLabel="Delete"
      destructive
      busy={acting}
      onConfirm={() => void confirmRemoveTournament()}
      onCancel={() => setShowDeleteConfirm(false)}
    />
    </>
  );
}
